/**
 * Finance Discovery — pure-TS step list + the M2 archetype derivation.
 *
 * Extracted from the prototype's `use-finance-discovery-store.ts` (a `'use client'` zustand
 * store): the store file mixes the React/zustand store with these PURE data-layer pieces. Only
 * the pure pieces live here so screens + tests can import them without pulling in the store.
 *
 *   • STEPS / Step          → the ordered step sequence (the registry keys off this)
 *   • Archetype             → alias of `ArchetypeId` (the three M2 orientations)
 *   • M2SliderAnswers       → the per-prompt slider-value map `deriveArchetype` consumes
 *   • deriveArchetype       → the EXACT `scoreM2` algorithm from the original prototype
 *
 * Source of truth: docs/EXPERIENCE-MODEL.md.
 */

import type { ArchetypeId } from './archetypes';
import { type M2Answer, sliderToAnswer } from './m2-questions';

/**
 * Ordered step sequence matching Vinicius's FINAL Figma flow (fileKey 69s7irJPnDrpGauf3SqlzS).
 * The registry keys a component off each step; `next`/`back` walk this array. The module-answer
 * field shapes are the locked data contract eng should treat as real.
 */
export const STEPS = [
  'welcome',
  'm1-intro',
  'm1-activity',
  'm1-results',
  'm1-complete',
  'm2-intro',
  'm2-activity',
  'm2-results',
  'm2-complete',
  'm3-intro',
  'm3-reading',
  'm3-activity',
  'm3-complete',
  'm4-intro',
  'm4-selection',
  'm4-activity',
  'm4-complete',
  'recommendations',
] as const;
export type Step = (typeof STEPS)[number];

/**
 * The three finance orientations the M2 sliders resolve to (the canonical set from the
 * original prototype): `corp` (THE SCALER) / `invest` (THE ANALYST) / `gen` (THE POLYMATH).
 */
export type Archetype = ArchetypeId;

/** M2 — "Money Mindset": slider positions (0–6) keyed by prompt id. */
export type M2SliderAnswers = Record<string, number>;

/**
 * Derive the M2 archetype from the four slider answers — the EXACT `scoreM2` algorithm
 * from the original prototype (app.js L502-507). Count the corp-lean ('A') answers across
 * the questions: each slider value ≤2 is an 'A' (the scored corp option), value 3 is 'gen'
 * (center, counts as neither), value ≥4 is 'B' (invest). Then:
 *   ≥3 corp answers → 'corp', ≤1 → 'invest', exactly 2 → 'gen'.
 *
 * Accepts the raw `M2SliderAnswers` map (value-per-question) and returns the orientation.
 * Missing/unset questions simply don't contribute an 'A', matching the original (which
 * scores `null`s as non-'A').
 *
 * EMPTY/never-answered M2 (no keys, no signal) falls back to 'gen' (THE POLYMATH) — the
 * documented neutral default the callers expect (`m2-results` / `recommendations` use
 * `stored ?? deriveArchetype(...)`). This is checked BEFORE the corpCount thresholds so an
 * empty map doesn't fall through to the ≤1 → 'invest' branch. A non-empty map (even one of
 * all-center 'gen' answers) keeps the existing corp/invest/gen scoring unchanged.
 */
export function deriveArchetype(m2Answers: M2SliderAnswers): Archetype {
  // Never-answered M2 → the generalist default (not 'invest'); see doc above.
  if (Object.keys(m2Answers).length === 0) {
    return 'gen';
  }
  const answers: M2Answer[] = Object.values(m2Answers).map(sliderToAnswer);
  const corpCount = answers.filter((x) => x === 'A').length;
  if (corpCount >= 3) {
    return 'corp';
  }
  if (corpCount <= 1) {
    return 'invest';
  }
  return 'gen';
}
